import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'
import { Capacitor } from '@capacitor/core'

/**
 * Determina a rota inicial para usuários já autenticados.
 * - Apps nativos sempre vão para o módulo de usuário.
 * - Se a navegação estiver dentro de /usuario ou o usuário tiver role "usuario",
 *   priorizamos o dashboard mobile em vez do dashboard geral.
 */
function getAuthenticatedHomeRoute(to: RouteLocationNormalized, role?: string) {
  if (Capacitor.isNativePlatform()) {
    return { name: 'UsuarioHome' }
  }

  if (to.path.startsWith('/usuario') || role === 'usuario') {
    return { name: 'UsuarioHome' }
  }

  return { name: 'Dashboard' }
}

function isUsuarioSurface(to: RouteLocationNormalized) {
  const targetPath = typeof to.path === 'string' ? to.path : ''
  return Capacitor.isNativePlatform() || targetPath.startsWith('/usuario')
}

/**
 * Determina qual página de login usar baseado na rota de destino
 */
function getLoginRoute(to: RouteLocationNormalized) {
  const targetPath = typeof to.path === 'string' ? to.path : ''

  // Em apps nativos, SEMPRE usar a página de login de usuário
  if (Capacitor.isNativePlatform()) {
    return { name: 'UsuarioLogin', query: { redirect: to.fullPath } }
  }

  // Se a rota é do módulo /usuario, usar a página de login específica
  if (targetPath.startsWith('/usuario')) {
    return { name: 'UsuarioLogin', query: { redirect: to.fullPath } }
  }
  // Caso contrário, usar a página de login genérica
  return { name: 'Login', query: { redirect: to.fullPath } }
}

/**
 * Guard de Autenticação
 * Verifica se o usuário está autenticado antes de acessar rotas protegidas
 */
export const authGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()

  try {
    const hasToken = !!authStore.token || !!localStorage.getItem('auth_token') || !!authStore.isAuthenticated

    if (!hasToken) {
      next(getLoginRoute(to))
      return
    }

    // Em apps nativos o token costuma estar disponível antes do usuário carregado.
    // Garantimos a hidratação do usuário antes de montar páginas que dependem dele.
    if (!authStore.user) {
      try {
        const isValid = await authStore.checkAuth()
        if (!isValid) {
          authStore.logout()
          notificationStore.showError('Sessão expirada. Faça login novamente.')
          next(getLoginRoute(to))
          return
        }
      } catch (error) {
        console.error('Erro na validação do token:', error)
        authStore.logout()
        notificationStore.showError('Erro na validação da sessão.')
        next(getLoginRoute(to))
        return
      }
    }

    // Usuário autenticado, continuar
    next()
  } catch (error) {
    console.error('Erro no guard de autenticação:', error)
    authStore.logout()
    notificationStore.showError('Erro de autenticação.')
    next(getLoginRoute(to))
  }
}

/**
 * Guard para rotas que requerem usuário não autenticado (guest)
 */
export const guestGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  try {
    const hasToken = !!authStore.token || !!localStorage.getItem('auth_token')

    // Sem token, manter em rota guest.
    if (!hasToken) {
      next()
      return
    }

    // Se temos token mas o usuário ainda não foi hidratado, validar sessão.
    if (!authStore.user) {
      const isValid = await authStore.checkAuth()
      if (!isValid) {
        next()
        return
      }
    }

    // O app do Usuário Bloop não deve reutilizar sessões de Oficina, Mecânico ou Admin.
    const role = authStore.user?.role
    if (isUsuarioSurface(to) && role !== 'usuario') {
      await authStore.logout({ redirect: false })
      next()
      return
    }

    // Sessão válida e compatível: usuário não deve ver tela de login.
    next(getAuthenticatedHomeRoute(to, role))
  } catch (error) {
    console.error('Erro no guest guard:', error)
    next()
  }
}
